import { logEvent } from 'firebase/analytics';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { analytics } from '../../firebase';
import { useBoolean } from '../utils/use-boolean';
import { useErrorContext } from '../../components/error';
import { useAuthContext } from '../../auth/auth-provider';
import { ResourceRecommendations, PaginatedResources } from '../../services/learning-api';
import { BaseResourceTableData } from '../../components/shared/resource-table-types';
import { PutUserResourceInteractionBody } from '../../services/learning-api/schema';
import {
  usePostHideRecommendation,
  usePutUserResourceInteraction,
} from '../query/learning-api/use-resource';

type ProgressStatus = PutUserResourceInteractionBody['status'];

export interface UseResourceTableProps {
  resources: PaginatedResources[] | ResourceRecommendations[];
  source?: string;
  enableRatingSystem?: boolean;
  getCategoryColor: (title: string) => string;
}

export const useResourceTable = ({
  resources,
  source,
  enableRatingSystem = false,
}: UseResourceTableProps) => {
  const { userId } = useAuthContext();
  const { setError } = useErrorContext();

  // Modal states
  const resourceTypeModal = useBoolean(false);
  const resourceLocationModal = useBoolean(false);
  const progressModal = useBoolean(false);
  const likesModal = useBoolean(false);
  const hideRecommendationModal = useBoolean(false);

  // Component states
  const [evaluateResourceModalOpen, setEvaluateResourceModalOpen] = useState(false);
  const [resourceToEvaluate, setResourceToEvaluate] = useState<string | null>(null);
  const [likeLoading, setLikeLoading] = useState<{ [id: string]: boolean }>({});
  const [selectedExternalResource, setSelectedExternalResource] = useState<{
    data: BaseResourceTableData;
    url: string;
  } | null>(null);

  const { putUserResourceInteraction } = usePutUserResourceInteraction({
    onError: (error) => {
      console.error('Error during saving the interaction:', error.message);
      setError({
        code: 500,
        message: 'Failed user interaction',
      });
    },
  });

  const initialLikes = useMemo(
    () =>
      Object.fromEntries(
        resources.map((r) => [
          r.id,
          {
            liked: !!r.isLikedByUser,
            count: r.totalLikes ?? 0,
          },
        ])
      ) as { [id: string]: { liked: boolean; count: number } },
    [resources]
  );

  const [likes, setLikes] = useState<{ [id: string]: { liked: boolean; count: number } }>(
    initialLikes
  );

  useEffect(() => {
    setLikes((prev) => ({ ...initialLikes, ...prev }));
  }, [initialLikes]);

  const [progress, setProgress] = useState<{ [id: string]: ProgressStatus }>({});

  // Progress options
  const progressOptions = [
    { label: 'Not started', value: 'NOT_STARTED' },
    { label: 'In progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
  ];

  // Update progress state
  const updateProgressState = useCallback(() => {
    const newProgress: { [id: string]: ProgressStatus } = {};

    for (const resource of resources) {
      newProgress[resource.id] = resource.userProgressStatus ?? 'NOT_STARTED';
    }

    setProgress((prev) => {
      const updated: { [id: string]: ProgressStatus } = { ...prev, ...newProgress };

      const validIds = new Set(resources.map((res) => res.id));
      for (const id of Object.keys(updated)) {
        if (!validIds.has(id)) {
          delete updated[id];
        }
      }

      return updated;
    });
  }, [resources]);

  useEffect(() => {
    updateProgressState();
  }, [resources, updateProgressState]);

  // Event handlers
  const handleLikeClick = useCallback(
    async (id: string) => {
      setLikeLoading((prev) => ({ ...prev, [id]: true }));

      const resource = resources.find((r) => r.id === id);
      const currentStatus = progress[id] || 'NOT_STARTED';
      const newLikeValue = !(likes[id]?.liked ?? !!resource?.isLikedByUser);

      try {
        await putUserResourceInteraction({
          resource_id: id,
          liked: newLikeValue,
          status: currentStatus as PutUserResourceInteractionBody['status'],
        });

        setLikes((prev) => {
          const currentCount = prev[id]?.count ?? resource?.totalLikes ?? 0;
          return {
            ...prev,
            [id]: {
              liked: newLikeValue,
              count: newLikeValue ? currentCount + 1 : Math.max(0, currentCount - 1),
            },
          };
        });
        const action = newLikeValue ? 'add_resource_likes' : 'remove_resource_likes';
        const currentCount = likes[id]?.count ?? resource?.totalLikes ?? 0;
        logEvent(analytics, action, {
          item_id: id,
          total_likes: newLikeValue ? currentCount + 1 : currentCount - 1,
          custom_user_id: userId,
        });
      } catch (error: any) {
        console.error('Error during interaction:', error.message);
      } finally {
        setLikeLoading((prev) => ({ ...prev, [id]: false }));
      }
    },
    [resources, progress, likes, putUserResourceInteraction, userId]
  );

  const handleProgressChange = useCallback(
    async (id: string, value: ProgressStatus) => {
      setProgress((prev) => ({ ...prev, [id]: value }));

      const resource = resources.find((r) => r.id === id);
      const likedNow = likes[id]?.liked ?? !!resource?.isLikedByUser;

      try {
        await putUserResourceInteraction({
          resource_id: id,
          liked: likedNow,
          status: value as ProgressStatus,
        });

        if (value === 'COMPLETED' && enableRatingSystem && !likedNow) {
          setResourceToEvaluate(id);
          setEvaluateResourceModalOpen(true);
        }
        logEvent(analytics, 'change_resource_status', {
          item_id: id,
          progress_status: value,
          custom_user_id: userId,
        });
      } catch (error: any) {
        console.error('Error updating progress status:', error.message);
      }
    },
    [putUserResourceInteraction, enableRatingSystem, likes, userId, resources]
  );

  const handleViewClick = useCallback(
    (data: BaseResourceTableData) => {
      if (!data.isInternal && data.personalLogin) {
        setSelectedExternalResource({ data, url: data.url });
        return;
      }

      logEvent(analytics, 'view_resource_item', {
        item_id: data.id,
        item_name: data.title,
        media_type: data.mediaType,
        location: 'internal',
        custom_user_id: userId,
        viewed_from: source,
      });

      window.open(data.url, '_blank');
    },
    [userId, source]
  );

  const handleExternalContinue = useCallback(() => {
    if (!selectedExternalResource) return;

    const { data, url } = selectedExternalResource;
    logEvent(analytics, 'view_resource_item', {
      item_id: data.id,
      item_name: data.title,
      media_type: data.mediaType,
      location: 'external',
      custom_user_id: userId,
      viewed_from: source,
    });

    window.open(url, '_blank');
    setSelectedExternalResource(null);
  }, [selectedExternalResource, userId, source]);

  const getProgressDropdownClass = useCallback((progressValue: string) => {
    switch (progressValue) {
      case 'COMPLETED':
        return 'progressDropdown progress-completed';
      case 'IN_PROGRESS':
        return 'progressDropdown progress-inprogress';
      case 'NOT_STARTED':
      default:
        return 'progressDropdown progress-notstarted';
    }
  }, []);

  // Handle hiding of learning resource recommendations
  const { postHideRecommendation } = usePostHideRecommendation();
  const [recommendationToHide, setRecommendationToHide] = useState(' ');

  const confirmHideRecommendation = useCallback(
    async (recommendationToHide: string) => {
      try {
        await postHideRecommendation(recommendationToHide);

        logEvent(analytics, 'hide_recommendation', {
          resource_id: recommendationToHide,
          user_id: userId,
        });
      } catch (err) {
        console.error('Failed to hide learning recommendation', err);
      } finally {
        hideRecommendationModal.onFalse();
      }
    },
    [hideRecommendationModal, postHideRecommendation, userId]
  );

  // Create table data

  const hasRecommendationHidden = (
    resource: PaginatedResources | ResourceRecommendations
  ): resource is ResourceRecommendations => {
    return 'recommendationHidden' in resource;
  };

  const tableData = useMemo(() => {
    return resources
      .filter(
        (resource) => !hasRecommendationHidden(resource) || resource.recommendationHidden === false
      )
      .map((resource: any) => {
        const {
          id,
          title,
          mediaType,
          skillCategory,
          skillLevel,
          url,
          isInternal,
          personalLogin,
          totalLikes,
          isLikedByUser,
          isBookmarked,
          isRoleDeptMatched,
        } = resource;

        const like = likes[id] ?? { liked: !!isLikedByUser, count: totalLikes ?? 0 };

        return {
          id,
          title,
          mediaType,
          isInternal,
          personalLogin,
          category: skillCategory?.[0]?.title || '',
          skillLevel,
          url,
          liked: like.liked,
          isBookmarked,
          likeCount: like.count,
          userProgressStatus: (progress[id] || 'NOT_STARTED') as ProgressStatus,
          isRoleDeptMatched,
        };
      });
  }, [resources, likes, progress]);

  return {
    // State
    likes,
    progress,
    likeLoading,
    selectedExternalResource,
    recommendationToHide,
    evaluateResourceModalOpen,
    resourceToEvaluate,
    progressOptions,
    tableData,

    // Modal states
    resourceTypeModal,
    resourceLocationModal,
    progressModal,
    likesModal,
    hideRecommendationModal,

    // Event handlers
    handleLikeClick,
    handleProgressChange,
    handleViewClick,
    handleExternalContinue,
    getProgressDropdownClass,
    confirmHideRecommendation,

    // Setters
    setSelectedExternalResource,
    setEvaluateResourceModalOpen,
    setResourceToEvaluate,
    setRecommendationToHide,
  };
};
